import { useSelector } from "@legendapp/state/react";
import { displayPercentageDiscount } from "@website/accommodation-ui/helpers/calculatePercentageDiscount";
import { getTracking } from "@website/store/tracking";
import { InfoChips } from "@website/ui/info-chips";
import { deepCompare } from "@website/utils/deep-compare";
import { numberConvert } from "@website/utils/number-convert";
import { numberSeparator } from "@website/utils/number-separator";
import { SnappProDiscountChip } from "@website/website-ui/snapp-pro-discount-chip";
import { useCompatRouter } from "@website/widget-v2/helpers/compat-router.hook";
import { FC, memo, useMemo } from "react";
import { HotelCardPricingPropertiesType } from "./hotel-card-pricing.types";

const Component: FC<HotelCardPricingPropertiesType> = (properties) => {
  const router = useCompatRouter();
  const { date_from, date_to } = router.query;
  const {
    price,
    priceOff = 0,
    size = "responsive",
    discountPercentage,
    landing = false,
    className,
    basePrice,
    basePriceOff,
    hasSnappProDiscount
  } = properties;
  const experiment = useSelector(getTracking().experiment);
  const hasDateAndPassengers = Boolean(
    date_from &&
      date_to &&
      router?.query?.["room_capacity"] &&
      Number(router?.query?.["room_capacity"]) > 0
  );

  const isVillaPricingExperiment =
    process.env.NEXT_PUBLIC_APP_NAME === "villa" &&
    experiment["experiment_villa_pricing"] === true;

  const priceBasedOnExperiment = isVillaPricingExperiment
    ? hasDateAndPassengers
      ? price
      : basePrice
    : price;

  const finalPrice = isVillaPricingExperiment
    ? priceOff && price > priceOff
      ? priceOff
      : priceBasedOnExperiment
    : null;

  const discountText = useMemo(
    () =>
      landing
        ? `%${numberConvert(
            (Math.ceil(discountPercentage as number) * 100)?.toFixed(0),
            {
              locale: "fa"
            }
          )}`
        : displayPercentageDiscount(price, priceOff),
    [landing, discountPercentage, price, priceOff]
  );

  return (
    <div
      className={`${
        size === "small"
          ? "flex flex-col items-end justify-center"
          : `flex flex-col justify-end lg:justify-center ${
              size === "xSmall"
                ? "items-start gap-0"
                : "items-end lg:items-center"
            } `
      } `}
    >
      {priceOff && price > priceOff && size !== "small" && (
        <>
          {process.env.NEXT_PUBLIC_APP_NAME === "villa" &&
          experiment["experiment_villa_pricing"] === true ? null : (
            <div
              className={`flex w-full items-center ${
                size === "xSmall" ? "gap-1" : "justify-end gap-2"
              }`}
            >
              {priceOff !== price && (
                <>
                  <span
                    className={`text-on-surface-disable line-through ${
                      size === "xSmall" ? "text-overline" : "text-body-2"
                    }`}
                  >
                    {numberConvert(numberSeparator(price), {
                      locale: router.locale || "fa"
                    })}
                  </span>
                  {hasSnappProDiscount ? (
                    <SnappProDiscountChip
                      percentage={discountPercentage as number}
                    />
                  ) : (
                    <InfoChips
                      className="!w-[32px] justify-center !p-[2px]"
                      size={size === "xSmall" ? "compact" : "medium"}
                      text={
                        landing
                          ? `%${numberConvert(
                              (
                                Math.ceil(discountPercentage as number) * 100
                              )?.toFixed(0),
                              {
                                locale: router.locale || "fa"
                              }
                            )}`
                          : displayPercentageDiscount(price, priceOff)
                      }
                      variant={
                        size === "xSmall" ? "round-accent" : "round-accent"
                      }
                    />
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
      {basePriceOff &&
      basePrice &&
      basePrice > basePriceOff &&
      size === "small" ? (
        <div className="flex w-full items-center justify-end gap-2 lg:justify-center">
          {basePriceOff !== basePrice && (
            <>
              <span className="text-on-surface-disable text-body-2 line-through">
                {numberConvert(numberSeparator(basePrice), {
                  locale: router.locale || "fa"
                })}
              </span>
              {hasSnappProDiscount ? (
                <SnappProDiscountChip
                  percentage={discountPercentage as number}
                />
              ) : (
                <InfoChips
                  className="!w-[30px] justify-center !p-[2px]"
                  size="medium"
                  variant="round-accent"
                  text={discountText}
                />
              )}
            </>
          )}
        </div>
      ) : null}
      <span
        className={`${className} ${
          size === "xSmall"
            ? "text-body-2 w-full font-bold"
            : size === "small"
              ? "text-on-surface-high-emphasis text-subtitle-1 font-bold"
              : "text-on-surface-high-emphasis text-headline-6 font-bold"
        }`}
        itemProp="priceRange"
      >
        {price ? (
          size === "small" && basePrice && !isVillaPricingExperiment ? (
            <div className="flex flex-col items-end gap-1">
              <div>
                {numberConvert(numberSeparator(basePrice), {
                  locale: router.locale || "fa"
                })}
                <span className="text-subtitle-2 px-1">تومان</span>
              </div>
            </div>
          ) : (
            <>
              {numberConvert(
                numberSeparator(
                  isVillaPricingExperiment
                    ? finalPrice
                    : priceOff && price > priceOff
                      ? priceOff
                      : price
                ),
                {
                  locale: router.locale || "fa"
                }
              )}
              <span
                className={`px-1 ${
                  size === "xSmall" ? "text-body-2" : "text-subtitle-2"
                }`}
              >
                تومان
              </span>
            </>
          )
        ) : (
          <>ناموجود</>
        )}
      </span>
    </div>
  );
};

export const HotelCardPricing = memo(Component, deepCompare);
